import { useEffect, useRef, useState } from "react";
import { apiFetch, planIntoScenario, TENANT } from "../api.js";

/**
 * The front door for making a scenario.
 *
 * It used to write a row the moment you clicked: a byte-identical copy of the
 * board named "What-if 14:32". Nothing differed, so there was nothing to look
 * at, and the click read as a no-op while drafts accumulated. Two things fix
 * that — asking what the scenario is *for*, and writing nothing until you
 * commit, so Cancel genuinely cancels.
 *
 * Planning is the primary path because a scenario should answer a question.
 * The manual copy stays, one step down, because testing one small idea by
 * dragging two phases is a real way to work — it just has to be named too.
 */
export function NewScenario({
  open,
  onClose,
  onCreated,
  role,
}: {
  open: boolean;
  onClose: () => void;
  /** Called with the new scenario, so the caller can switch to it and show the diff. */
  onCreated: (scenarioId: string, note?: string) => void;
  /** The caller's role, so the dialog can refuse before doing five seconds of work. */
  role: string | undefined;
}) {
  const [intent, setIntent] = useState("");
  const [name, setName] = useState("");
  const [touchedName, setTouchedName] = useState(false);
  const [busy, setBusy] = useState<null | "plan" | "copy">(null);
  const [err, setErr] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const input = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) return;
    setIntent(""); setName(""); setTouchedName(false);
    setErr(null); setBusy(null); setProgress(null);
    setTimeout(() => input.current?.focus(), 30);
  }, [open]);

  // Suggest a name from the intent until the user writes their own. Naming a
  // scenario is a chore people skip, and unnamed scenarios cannot be compared.
  useEffect(() => {
    if (touchedName) return;
    setName(suggestName(intent));
  }, [intent, touchedName]);

  // Escape closes the dialog from anywhere in it — not only from the describe
  // box, which a manager no longer has.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Planning from a sentence runs the planner, which is admin-only on the
  // server. Starting from today's schedule only needs a draft to be made, which
  // a manager may do. Finding a refusal out after the planner has run costs
  // five seconds and reads as the button doing nothing, so say it before
  // anything is spent.
  const canPlan = role === "admin";
  const canCopy = role === "admin" || role === "manager";

  async function plan() {
    if (!intent.trim()) return;
    setBusy("plan"); setErr(null);
    setProgress("Reading your intent…");
    const started = Date.now();
    const tick = setInterval(
      () => setProgress(`Planning and solving… ${Math.round((Date.now() - started) / 1000)}s`),
      1000,
    );
    try {
      // The scenario exists first, then the intent joins it as its first
      // requirement. Two steps rather than one because a scenario is now a
      // workspace you keep adding to, not a by-product of a single run.
      const made = await apiFetch("/v1/sentry/scenarios", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ tenantId: TENANT, name: name.trim() || "What-if" }),
      });
      const madeJson = await made.json().catch(() => ({}));
      if (!made.ok) { setErr(madeJson.error ?? `HTTP ${made.status}`); return; }
      const newScenarioId = madeJson.scenarioId as string;

      const r = await planIntoScenario(newScenarioId, intent);
      if (!r.ok) {
        // The scenario exists but has nothing in it. Open it anyway: the
        // composer is right there, and a dialog that swallows the scenario it
        // just made is worse than one that hands it over half-finished.
        onCreated(newScenarioId, r.error);
        onClose();
        return;
      }
      onCreated(newScenarioId, r.note);
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      clearInterval(tick);
      setBusy(null); setProgress(null);
    }
  }

  async function copy() {
    const n = name.trim() || "Working copy";
    setBusy("copy"); setErr(null);
    try {
      const res = await apiFetch("/v1/sentry/scenarios", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ tenantId: TENANT, name: n }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) { setErr(j.error ?? `HTTP ${res.status}`); return; }
      onCreated(j.scenarioId, "Started from today's schedule — nothing has changed yet. Drag work to try something.");
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="modal-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="New scenario">
        <div className="modal-head">
          <span>New scenario</span>
          <button className="btn ghost" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {!canCopy && (
          <div className="modal-err">
            You are signed in as <b>{role ?? "…"}</b>, and creating a scenario needs a manager or an
            admin. An admin sets access roles on the People screen.
          </div>
        )}

        {/* Not rendered at all for a manager: a box they cannot use is a box they type into. */}
        {canPlan && (
        <label className="fld">
          <span>What do you want this scenario to do?</span>
          <textarea
            ref={input}
            rows={3}
            value={intent}
            placeholder="get 216311 back on track without pushing 221965 or 220081 any later"
            onChange={(e) => setIntent(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void plan();
              if (e.key === "Escape") onClose();
            }}
          />
        </label>
        )}

        <label className="fld row">
          <span>Name</span>
          <input
            value={name}
            placeholder={canPlan ? "Recover 216311" : "e.g. Week 41 — move Eric's work"}
            autoFocus={!canPlan}
            onChange={(e) => { setTouchedName(true); setName(e.target.value); }}
            onKeyDown={(e) => { if (e.key === "Enter" && !canPlan && canCopy) void copy(); }}
          />
        </label>

        {canCopy && !canPlan && (
          <div className="modal-note">
            Planning from a description is an admin's tool. As a manager you start from
            today's schedule and move work within your own teams and projects.
          </div>
        )}
        {err && <div className="modal-err">{err}</div>}
        {progress && <div className="modal-note">{progress}</div>}

        <div className="modal-actions">
          <button className="btn" disabled={!!busy} onClick={onClose}>Cancel</button>
          {canPlan ? (
            <button className="btn on" disabled={!!busy || !intent.trim()} onClick={() => void plan()}>
              {busy === "plan" ? "Planning…" : "Plan it  ⌘⏎"}
            </button>
          ) : (
            <button className="btn on" disabled={!!busy || !canCopy} onClick={() => void copy()}>
              {busy === "copy" ? "Creating…" : "Start from today's schedule"}
            </button>
          )}
        </div>

        {canPlan && (
          <div className="modal-alt">
            <button className="linkish" disabled={!!busy || !canCopy} onClick={() => void copy()}>
              {busy === "copy" ? "Creating…" : "or start from today's schedule and move work myself →"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A short label from a sentence. Order ids first — "Recover 216311" is what
 * someone scans a scenario list for, and the rest of the sentence is noise at
 * that size.
 */
export function suggestName(intent: string): string {
  const t = intent.trim();
  if (!t) return "";
  const ids = [...t.matchAll(/\b\d{5,7}\b/g)].map((m) => m[0]);
  const verb = /\b(recover|unblock|pull in|pull|delay|push|protect|rebalance|level|finish|free up|catch up)\b/i.exec(t);
  const cap = (s: string) => s.replace(/^./, (c) => c.toUpperCase());
  if (ids.length) {
    const lead = verb ? cap(verb[1]!) : "What-if";
    return `${lead} ${ids.slice(0, 2).join(" & ")}`;
  }
  const words = cap(t.split(/\s+/).slice(0, 5).join(" "));
  return words.length > 42 ? `${words.slice(0, 42)}…` : words;
}
